import { AnimalTypeEnum, ProfessionalTypeEnum } from '@gql/graphql';
import { Button, InfoBox, InputRadioField, InputSelectField } from '@presentation/components';
import { SideSkeletonBox } from '@presentation/pages/Pro/components/PageSkeleton/styled';
import { useTranslation } from '@presentation/utils';
import { Form, useFormikContext } from 'formik';
import type { FC } from 'react';
import { useCallback } from 'react';
import { InputLabel } from '../InputLabel';
import { WorkerAvailabilitiesInput } from './components/WorkerAvailabilitiesInput';
import * as Styled from './styled';
import type { BookingSlotPickerFormValues, BookingSlotPickerProps } from './types';

export * from './types';

// Whole component has very bad data/component structure, refactor this
export const BookingSlotPicker: FC<BookingSlotPickerProps> = (props) => {
  const {
    breedOptions,
    serviceOptions,
    workerOptions,
    animalOptions,
    shownFields,
    updateMode = false,
    customFormTitle,
    disabled = false,
    warning,
    proType,
    loading = false,
  } = props;
  const { translate } = useTranslation();
  const showField = useCallback(
    (field: keyof BookingSlotPickerFormValues) => {
      return shownFields.includes(field);
    },
    [shownFields],
  );

  const fieldFormattedIndex = useCallback(
    (field: keyof BookingSlotPickerFormValues) => {
      return (shownFields.indexOf(field) + 1).toString();
    },
    [shownFields],
  );

  const { values, setFieldValue } = useFormikContext<BookingSlotPickerFormValues>();

  const serviceSelectDisabled =
    proType === ProfessionalTypeEnum.GROOMER &&
    (!values.animalType || (values.animalType === AnimalTypeEnum.DOG && !values.breedId));

  if (loading) return <SideSkeletonBox />;

  return (
    <Styled.Wrapper>
      <Styled.Header>
        <Styled.HeaderTitle>
          {customFormTitle || translate('proDetails.slotPicker.title')}
        </Styled.HeaderTitle>
        <Styled.HeaderSubitle>{translate('proDetails.slotPicker.subtitle')}</Styled.HeaderSubitle>
      </Styled.Header>
      {!!warning && <InfoBox>{warning}</InfoBox>}
      <Form>
        {showField('forMultiAnimals') && (
          <Styled.InputWrapper>
            <Styled.RoundedIcon active>{fieldFormattedIndex('forMultiAnimals')}</Styled.RoundedIcon>
            <InputLabel>{translate('common.inputs.multipleAnimals.placeholder')}</InputLabel>
            <InputRadioField<boolean>
              grow
              name="forMultiAnimals"
              disabled={disabled || updateMode}
              onSelect={() => {
                setFieldValue('animalType', undefined);
                setFieldValue('serviceId', undefined);
                setFieldValue('breedId', undefined);
                setFieldValue('workerId', undefined);
              }}
              options={[
                {
                  value: false,
                  label: translate('common.inputs.multipleAnimals.no'),
                },
                {
                  value: true,
                  label: translate('common.inputs.multipleAnimals.yes'),
                },
              ]}
            />
          </Styled.InputWrapper>
        )}

        {showField('animalType') && (
          <Styled.InputWrapper>
            <Styled.RoundedIcon active>{fieldFormattedIndex('animalType')}</Styled.RoundedIcon>
            <InputLabel>{translate('common.inputs.animalType.label')}</InputLabel>
            <InputSelectField<AnimalTypeEnum>
              hasDropdownIcon
              name="animalType"
              placeholder={translate('common.inputs.animalType.placeholder')}
              disabled={disabled || updateMode}
              onSelect={() => {
                setFieldValue('serviceId', undefined);
                setFieldValue('breedId', undefined);
                setFieldValue('workerId', undefined);
              }}
              options={animalOptions}
            />
          </Styled.InputWrapper>
        )}

        {showField('breedId') && (
          <Styled.InputWrapper>
            <Styled.RoundedIcon active={!!values.breedId}>
              {fieldFormattedIndex('breedId')}
            </Styled.RoundedIcon>
            <InputLabel>{translate('common.inputs.breed.label')}</InputLabel>
            <InputSelectField
              hasDropdownIcon
              name="breedId"
              placeholder={translate('common.inputs.breed.placeholder')}
              disabled={disabled || updateMode || !values.animalType}
              options={breedOptions}
              onSelect={() => {
                setFieldValue('serviceId', undefined);
                setFieldValue('workerId', undefined);
              }}
            />
          </Styled.InputWrapper>
        )}

        {showField('serviceId') && (
          <Styled.InputWrapper>
            <Styled.RoundedIcon active={!!values.serviceId}>
              {fieldFormattedIndex('serviceId')}
            </Styled.RoundedIcon>
            <InputLabel>{translate('common.inputs.service.label')}</InputLabel>
            <InputSelectField<string>
              hasDropdownIcon
              dropdownProps={{
                showEmptyMessage: true,
                emptyProps: {
                  title: translate('common.inputs.service.empty'),
                },
              }}
              useSearch={false}
              name="serviceId"
              placeholder={translate('common.inputs.service.placeholder')}
              disabled={disabled || updateMode || serviceSelectDisabled}
              options={serviceOptions}
              onSelect={() => {
                setFieldValue('workerId', undefined);
              }}
            />
          </Styled.InputWrapper>
        )}

        {showField('workerId') && (
          <Styled.InputWrapper>
            <Styled.RoundedIcon active={!!values.serviceId && !!values.workerId}>
              {fieldFormattedIndex('workerId')}
            </Styled.RoundedIcon>
            <InputLabel>
              {translate(
                `common.inputs.${
                  proType === ProfessionalTypeEnum.GROOMER ? 'workerGroomer' : 'workerVet'
                }.label`,
              )}
            </InputLabel>
            <InputSelectField<number | 'any'>
              dropdownProps={{
                showEmptyMessage: true,
              }}
              hasDropdownIcon
              name="workerId"
              disabled={disabled || updateMode || !values.serviceId}
              useSearch={false}
              options={workerOptions}
              placeholder={translate(
                `common.inputs.${
                  proType === ProfessionalTypeEnum.GROOMER ? 'workerGroomer' : 'workerVet'
                }.placeholder`,
              )}
            />
          </Styled.InputWrapper>
        )}

        {showField('bookingStart') && (
          <Styled.InputWrapper>
            <Styled.RoundedIcon active={!!values.bookingStart} last>
              {fieldFormattedIndex('bookingStart')}
            </Styled.RoundedIcon>
            <InputLabel>{translate('common.inputs.slot.label')}</InputLabel>
            <WorkerAvailabilitiesInput {...props} />
          </Styled.InputWrapper>
        )}

        <Button size="large" disabled={disabled || !values.bookingStart} color="blue" type="submit">
          {translate('common.buttons.continue')}
        </Button>
      </Form>
    </Styled.Wrapper>
  );
};
